import { useQuery, useMutation, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { applicationApi } from '../api/endpoints/application';
import { generateIdempotencyKey } from '../lib/idempotency';
import type {
    ApplicationListItemLocal,
    ApplicationDetailLocal,
    CreateApplicationRequestLocal,
    UpdateApplicationRequestLocal,
} from '../api/types/ApplicationLocal';

/**
 * Hooks TanStack Query del modelo "Aplicación" (plantilla). Convención de queryKey del repo:
 * `['applications', ...]` (espeja `['tenant', tid, ...]` / `['platform', ...]`). Las mutations
 * invalidan la lista (y el detalle, en update) como el resto del repo.
 *
 * El orden de presentación (alfabético por `displayName`, default del proyecto) lo resuelve el
 * consumidor de UI — aquí se devuelve la lista tal cual la sirve el backend.
 */

const APPLICATIONS_KEY = ['applications'] as const;

export function useApplications(): UseQueryResult<ApplicationListItemLocal[]> {
    return useQuery({
        queryKey: APPLICATIONS_KEY,
        queryFn: () => applicationApi.list(),
        select: (r) => r.items,
    });
}

export function useApplication(
    applicationId: string | undefined,
): UseQueryResult<ApplicationDetailLocal> {
    return useQuery({
        queryKey: ['applications', applicationId, 'detail'],
        queryFn: () => applicationApi.get(applicationId ?? ''),
        enabled: !!applicationId,
    });
}

export function useCreateApplication() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: CreateApplicationRequestLocal) =>
            applicationApi.create(body, { idempotencyKey: generateIdempotencyKey() }),
        onSuccess: async () => {
            await qc.invalidateQueries({ queryKey: APPLICATIONS_KEY });
        },
    });
}

export function useUpdateApplication(applicationId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: UpdateApplicationRequestLocal) =>
            applicationApi.update(applicationId, body),
        onSuccess: async () => {
            await Promise.all([
                qc.invalidateQueries({ queryKey: APPLICATIONS_KEY }),
                qc.invalidateQueries({ queryKey: ['applications', applicationId, 'detail'] }),
            ]);
        },
    });
}

// DEC-RBAC-088: hard delete de la app. Invalida la lista al terminar; el manejo del 409
// APPLICATION_HAS_TENANTS (mensaje "tiene N tenants") lo hace el call-site en su onError.
export function useDeleteApplication() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (applicationId: string) => applicationApi.delete(applicationId),
        onSuccess: async () => {
            await qc.invalidateQueries({ queryKey: APPLICATIONS_KEY });
        },
    });
}
